// @file Store for class information
import { captureFetchException } from '@@/bits/error_tracker'
import { ClassInformation as ClassInformationApi } from '@@/dashboard/padlet_api'
import { HttpCode } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import type { ClassInformation as ClassInformationType } from '@@/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { ref } from 'vue'

const useClassInformationStore = defineStore('classInformationStore', () => {
  const globalSnackbarStore = useGlobalSnackbarStore()

  const classInformation = ref<string>('')
  const isLoadingClassInformation = ref(false)

  const fetchClassInformation = async (): Promise<void> => {
    isLoadingClassInformation.value = true
    try {
      const response = await ClassInformationApi.fetchClassInformation()
      classInformation.value = (response.data as JsonAPIResource<ClassInformationType>).attributes.value
    } catch (error) {
      // 404 is expected when user hasn't set class information yet - don't track as error
      if (error.status !== HttpCode.NotFound) {
        captureFetchException(error, { source: 'ClassInformationStore' })
      }
    } finally {
      isLoadingClassInformation.value = false
    }
  }

  // Returns true when the write persisted, false when the API call failed (after
  // surfacing a snackbar). Callers that gate further state on a successful save
  // (e.g. marking the field "saved") must check this — the write swallows its
  // error, so a void return would let a failed save look successful.
  const upsertClassInformation = async ({ newClassInfo }: { newClassInfo: string }): Promise<boolean> => {
    const trimmedClassInfo = newClassInfo?.trim()
    isLoadingClassInformation.value = true

    try {
      await ClassInformationApi.upsert(trimmedClassInfo)
      classInformation.value = trimmedClassInfo
      return true
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'ClassInformationUpsertion' })
      return false
    } finally {
      isLoadingClassInformation.value = false
    }
  }

  return {
    classInformation,
    isLoadingClassInformation,
    fetchClassInformation,
    upsertClassInformation,
  }
})
export { useClassInformationStore }
